import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Grid,
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ListAlt as ListAltIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";

import { getConciliationKeys } from "modules/finops/conciliaciones/services/financieraService";
import FormImportacionLote from "../Form/FormImportacionLote";
import TableLoteFinanciera1 from "../TableLote/TableLoteFinanciera1";
import ModalPDF from "modules/finops/conciliaciones/lotesFinanciera/components/ModalPDF";

const colors = {
  primary: "#1e293b",
  secondary: "#0b4f6c",
  white: "#ffffff",
  success: "#27ae60",
  warning: "#da1c15ff",
  info: "#2980b9",
};

const HeaderLote = ({ refreshFlag, onError, onSuccess }) => {
  const [activeTab, setActiveTab] = useState(1);
  const [selectedConciliationId, setSelectedConciliationId] = useState("");

  // Filtros
  const [llaveSeleccionada, setLlaveSeleccionada] = useState(null);
  const [llaveOptions, setLlaveOptions] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  const [busquedaGlobal, setBusquedaGlobal] = useState("");
  const [busquedaDebounced, setBusquedaDebounced] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [statusOptions, setStatusOptions] = useState([]);
  const handleStatusOptionsChange = useCallback((options) => {
    setStatusOptions(options);
    setFiltroStatus((current) =>
      current === "todos" || options.some((option) => option.value === current) ? current : "todos"
    );
  }, []);
  const [filtrosActivos, setFiltrosActivos] = useState(false);

  // 🆕 Modal Ver Excel (usa ModalPDF)
  const [openVerExcel, setOpenVerExcel] = useState(false);
  const [selectedExcelData, setSelectedExcelData] = useState(null);
  const [selectedExcelKeyConcil, setSelectedExcelKeyConcil] = useState(null);

  const debounceTimeout = useRef(null);

  // ================================================
  // Cargar llaves
  // ================================================
  const loadConciliationKeys = useCallback(async () => {
    setLoadingKeys(true);
    try {
      const result = await getConciliationKeys();

      if (result.success) {
        const normalized = (result.data || []).map((item) => {
          if (typeof item === "string") {
            return { value: item, label: item, id: null, file: null };
          }
          const val = item.value ?? item.key_concil ?? item.llave ?? "";
          return {
            value: String(val),
            label: String(item.label ?? val),
            id: item.id ?? null,
            file: item.file ?? null,
          };
        });
        setLlaveOptions(normalized);
      } else {
        setLlaveOptions([]);
      }
    } catch (error) {
      console.error("Error cargando llaves:", error);
      setLlaveOptions([]);
    } finally {
      setLoadingKeys(false);
    }
  }, []);

  useEffect(() => {
    loadConciliationKeys();
  }, [loadConciliationKeys]);

  // ================================================
  // 🆕 Mapa de Excel por llave (solo Excel)
  // ================================================
  const excelByKeyConcil = useMemo(() => {
    const map = {};
    llaveOptions.forEach((opt) => {
      if (!opt?.value) return;
      const file = opt.file;
      if (!file) return;

      const mimetype = (file.mimetype || "").toLowerCase();
      const filename = (file.filename || file.originalname || "").toLowerCase();

      const isExcel =
        mimetype === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        mimetype === "application/vnd.ms-excel" ||
        filename.endsWith(".xlsx") ||
        filename.endsWith(".xls");

      if (isExcel && !map[opt.value]) {
        map[opt.value] = {
          id: opt.id,
          ...file,
        };
      }
    });
    return map;
  }, [llaveOptions]);

  // Debounce
  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    if (busquedaGlobal === "") {
      setBusquedaDebounced("");
      return;
    }

    debounceTimeout.current = setTimeout(() => {
      setBusquedaDebounced(busquedaGlobal);
    }, 500);

    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [busquedaGlobal]);

  // Filtros activos
  useEffect(() => {
    const hasFilters = !!(
      llaveSeleccionada ||
      (!llaveSeleccionada && (fechaInicio || fechaFin)) ||
      busquedaDebounced ||
      filtroStatus !== "todos"
    );
    setFiltrosActivos(hasFilters);
  }, [llaveSeleccionada, fechaInicio, fechaFin, busquedaDebounced, filtroStatus]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleImportSuccess = useCallback(
    (result) => {
      if (onSuccess) {
        onSuccess(result.message || "Registros importados exitosamente");
      }
    },
    [onSuccess]
  );

  const handleFiltrarLlave = useCallback(
    (key) => {
      const value = String(key || "").trim();
      if (!value) return;

      const existente = llaveOptions.find((opt) => opt.value === value);
      const opcion = existente || { value, label: value, id: null, file: null };

      if (!existente) {
        setLlaveOptions((prev) => [...prev, opcion]);
      }

      setBusquedaGlobal("");
      setBusquedaDebounced("");

      setLlaveSeleccionada(opcion);
    },
    [llaveOptions]
  );

  const clearFilters = () => {
    setLlaveSeleccionada(null);
    setFechaInicio("");
    setFechaFin("");
    setBusquedaGlobal("");
    setBusquedaDebounced("");
    setFiltroStatus("todos");
    setFiltrosActivos(false);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setBusquedaDebounced(busquedaGlobal);
    }
  };

  // ================================================
  // 🆕 Abrir Excel en el visor (mismo ModalPDF)
  // ================================================
  const handleVerExcelDesdeFila = useCallback(
    (row) => {
      const keyConcil = row?.key_concil || row?.llave;
      if (!keyConcil) return;

      const fileData = excelByKeyConcil[keyConcil];
      if (!fileData) return;

      setSelectedExcelKeyConcil(keyConcil);
      setSelectedExcelData({
        id: fileData.id,
        filename: fileData.originalname || fileData.filename || "Excel Financiera.xlsx",
        size: fileData.size || 0,
        mimetype: fileData.mimetype,
        filepath: fileData.path || "",
      });
      setOpenVerExcel(true);
    },
    [excelByKeyConcil]
  );

  const handleCerrarVerExcel = useCallback(() => {
    setOpenVerExcel(false);
    setSelectedExcelData(null);
    setSelectedExcelKeyConcil(null);
  }, []);

  const tabsConfig = [
    {
      label: "Importar Datos",
      icon: <CloudUploadIcon sx={{ fontSize: 20, mr: 1 }} />,
      value: 0,
    },
    {
      label: "Lista de Conciliaciones",
      icon: <ListAltIcon sx={{ fontSize: 20, mr: 1 }} />,
      value: 1,
    },
  ];

  const llaveValueParaTabla = llaveSeleccionada?.value || "";

  return (
    <MDBox position="relative" sx={{ mt: -2, mx: -3, py: 2, px: -2 }}>
      <Card
        sx={{
          mt: 1,
          mx: 3,
          py: 3,
          px: 3,
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #1e293b 0%, #0b4f6c 100%)",
            margin: -3,
            marginBottom: 3,
            padding: 3,
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={2}>
                <ListAltIcon sx={{ fontSize: 32, color: "rgba(255,255,255,0.8)" }} />
                <Box>
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    sx={{ color: "rgba(255, 255, 255)" }}
                  >
                    Conciliaciones Financieras
                  </MDTypography>
                  <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mt: 0.5 }}>
                    Importe y administre las conciliaciones financieras del sistema
                  </MDTypography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                {filtrosActivos && activeTab === 1 && (
                  <Chip
                    label="Filtros activos"
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.15)",
                      color: "rgba(255,255,255,0.8)",
                    }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
                background: "linear-gradient(90deg, #0b4f6c 0%, #1e7e6c 100%)",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minHeight: 48,
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: "rgba(11, 79, 108, 0.04)",
                  borderRadius: 1,
                },
                "&.Mui-selected": { color: "#0b4f6c" },
              },
            }}
          >
            {tabsConfig.map((tab) => (
              <Tab key={tab.value} icon={tab.icon} label={tab.label} iconPosition="start" />
            ))}
          </Tabs>
        </Box>

        {activeTab === 1 && (
          <Box sx={{ mb: 3, p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  placeholder="🔍 Buscar por ID, Nombre o Llave..."
                  value={busquedaGlobal}
                  onChange={(e) => setBusquedaGlobal(e.target.value)}
                  onKeyPress={handleKeyPress}
                  size="small"
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "44px",
                      backgroundColor: "#ffffff",
                      borderRadius: "8px",
                    },
                    "& input": {
                      padding: "8px 16px",
                      fontSize: "0.9rem",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#64748b" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {busquedaGlobal && busquedaGlobal !== busquedaDebounced && (
                          <Chip
                            label="Buscando..."
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              bgcolor: "#fff3e0",
                              color: "#e65100",
                              animation: "pulse 1s infinite",
                              "@keyframes pulse": {
                                "0%": { opacity: 1 },
                                "50%": { opacity: 0.5 },
                                "100%": { opacity: 1 },
                              },
                            }}
                          />
                        )}
                        {busquedaGlobal && (
                          <Button
                            size="small"
                            onClick={() => {
                              setBusquedaGlobal("");
                              setBusquedaDebounced("");
                            }}
                            sx={{
                              minWidth: "auto",
                              p: 0.5,
                              color: "#64748b",
                              "&:hover": { color: "#d32f2f" },
                            }}
                          >
                            <ClearIcon fontSize="small" />
                          </Button>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Autocomplete
                      options={llaveOptions}
                      loading={loadingKeys}
                      value={llaveSeleccionada}
                      onChange={(event, newValue) => {
                        setLlaveSeleccionada(newValue);
                      }}
                      getOptionLabel={(option) => {
                        if (!option) return "";
                        if (typeof option === "string") return option;
                        return option.label ?? option.value ?? "";
                      }}
                      isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return false;
                        return option.value === value.value;
                      }}
                      renderInput={(params) => {
                        const { InputLabelProps, InputProps, ...restParams } = params;
                        return (
                          <TextField
                            {...restParams}
                            label="Llave de Conciliación"
                            placeholder="Filtrar por llave..."
                            size="small"
                            sx={{ "& .MuiInputBase-root": { height: "40px" } }}
                            InputLabelProps={InputLabelProps}
                            InputProps={{
                              ...InputProps,
                              endAdornment: (
                                <>
                                  {loadingKeys ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        );
                      }}
                      renderOption={({ key, ...optionProps }, option) => (
                        <Box component="li" {...optionProps} key={option.value ?? key}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={option.label}
                              size="small"
                              sx={{
                                fontFamily: "monospace",
                                backgroundColor: `${colors.info}15`,
                                color: colors.info,
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                      noOptionsText={loadingKeys ? "Cargando..." : "No hay llaves disponibles"}
                      sx={{
                        "& .MuiAutocomplete-inputRoot": {
                          height: "40px",
                          padding: "0 14px",
                        },
                      }}
                    />
                  </Grid>

                  {!llaveSeleccionada && (
                    <>
                      <Grid item xs={6} md={2}>
                        <TextField
                          fullWidth
                          label="Creación desde"
                          type="date"
                          inputProps={{ max: fechaFin || undefined }}
                          value={fechaInicio}
                          onChange={(e) => setFechaInicio(e.target.value)}
                          size="small"
                          sx={{
                            "& .MuiInputBase-root": { height: "40px" },
                            "& input": { padding: "8px 12px" },
                          }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={6} md={2}>
                        <TextField
                          fullWidth
                          label="Creación hasta"
                          type="date"
                          inputProps={{ min: fechaInicio || undefined }}
                          error={Boolean(fechaInicio && fechaFin && fechaInicio > fechaFin)}
                          helperText={
                            fechaInicio && fechaFin && fechaInicio > fechaFin
                              ? "Revise el rango de fechas"
                              : ""
                          }
                          value={fechaFin}
                          onChange={(e) => setFechaFin(e.target.value)}
                          size="small"
                          sx={{
                            "& .MuiInputBase-root": { height: "40px" },
                            "& input": { padding: "8px 12px" },
                          }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Estado"
                      value={filtroStatus}
                      onChange={(event) => setFiltroStatus(event.target.value)}
                      sx={{ "& .MuiInputBase-root": { minHeight: 40 } }}
                    >
                      <MenuItem value="todos">Todos los estados</MenuItem>
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} md={1.5}>
                    {filtrosActivos && (
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        onClick={clearFilters}
                        startIcon={<ClearIcon />}
                        sx={{
                          height: "40px",
                          textTransform: "none",
                          color: "#d32f2f",
                          borderColor: "#d32f2f",
                          "&:hover": {
                            borderColor: "#b71c1c",
                            backgroundColor: "rgba(211, 47, 47, 0.04)",
                          },
                        }}
                      >
                        Limpiar todo
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {filtrosActivos && (
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {busquedaDebounced && (
                  <Chip
                    label={`Búsqueda: "${busquedaDebounced}"`}
                    size="small"
                    onDelete={() => {
                      setBusquedaGlobal("");
                      setBusquedaDebounced("");
                    }}
                    sx={{
                      bgcolor: `${colors.info}15`,
                      color: colors.info,
                      maxWidth: "300px",
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
                {llaveSeleccionada && (
                  <Chip
                    label={`Llave: ${llaveSeleccionada.label}`}
                    size="small"
                    onDelete={() => setLlaveSeleccionada(null)}
                    sx={{
                      bgcolor: `${colors.info}15`,
                      color: colors.info,
                      fontFamily: "monospace",
                    }}
                  />
                )}
                {!llaveSeleccionada && fechaInicio && (
                  <Chip
                    label={`Creación desde: ${fechaInicio}`}
                    size="small"
                    onDelete={() => setFechaInicio("")}
                    sx={{ bgcolor: "#e3f2fd", color: "#0b4f6c" }}
                  />
                )}
                {!llaveSeleccionada && fechaFin && (
                  <Chip
                    label={`Creación hasta: ${fechaFin}`}
                    size="small"
                    onDelete={() => setFechaFin("")}
                    sx={{ bgcolor: "#e3f2fd", color: "#0b4f6c" }}
                  />
                )}
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          {activeTab === 0 && (
            <Box sx={{ animation: "fadeIn 0.3s ease-in-out" }}>
              <FormImportacionLote onImportSuccess={handleImportSuccess} onError={onError} />
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ animation: "fadeIn 0.3s ease-in-out" }}>
              <TableLoteFinanciera1
                refreshFlag={refreshFlag}
                onError={onError}
                onSelectLote={setSelectedConciliationId}
                onFiltrarLlave={handleFiltrarLlave}
                onVerExcelLlave={handleVerExcelDesdeFila}
                excelByKeyConcil={excelByKeyConcil}
                llaveSeleccionada={llaveValueParaTabla}
                fechaInicio={llaveSeleccionada ? "" : fechaInicio}
                fechaFin={llaveSeleccionada ? "" : fechaFin}
                filtroVertical=""
                filtroBusqueda={busquedaDebounced}
                filtroStatus={filtroStatus}
                onStatusOptionsChange={handleStatusOptionsChange}
                tipoFiltro="global"
              />
            </Box>
          )}
        </Box>
      </Card>

      {/* 🆕 Ver Excel en el mismo visor de documentos */}
      <ModalPDF
        open={openVerExcel}
        onClose={handleCerrarVerExcel}
        keyConcil={selectedExcelKeyConcil}
        filesData={[]}
        excelFile={selectedExcelData}
      />
    </MDBox>
  );
};

HeaderLote.propTypes = {
  refreshFlag: PropTypes.bool,
  onError: PropTypes.func,
  onSuccess: PropTypes.func,
};

HeaderLote.defaultProps = {
  refreshFlag: false,
  onError: null,
  onSuccess: null,
};

export default HeaderLote;
